import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import useBanks from '@/hooks/useBanks';
import Blade from '@/components/ui/Blade/Blade';
import BladeSimpleSection from '@/components/ui/BladeSimpleSection/BladeSimpleSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { DebitHistoryItem } from '@/models/Debit';
import { CreditStandingOrderDetailsDtoIn, CreditHistoryItemDtoIn, BankStandingOrderDetailsDtoIn, BankHistoryItemDtoIn } from '@/models/StandingOrderDetails';
import { aptHistoryToBankHistory, isAptOrder } from '../aptStandingOrders';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import IconWithText from '@/components/ui/IconWithText/IconWithText';
import TextRowsSkeleton from '@/components/ui/Skeleton/TextRowsSkeleton';
import donation from '@/assets/icons/donation.svg';
import whatsapp from '@/assets/icons/whatsapp.svg';
import email from '@/assets/icons/email.svg';
import {
  activeStatusKey,
  getActiveStatus,
  parseDate,
  formatDate,
  formatRowMoney,
  formatCardExpiry,
  getLastChargeStatus,
  getCardExpiryState,
  getPaymentSourceLabel,
  getPaymentsPaid,
  getPaymentsTotal,
  getTotalPaid,
  isUnlimited,
  STANDING_ORDER_TYPE_CREDIT,
  STANDING_ORDER_TYPE_BANK,
} from '../standingOrderModel';
import Alert from '@/components/ui/Alert';
import CopyButton from '@/components/ui/CopyButton/CopyButton';
import AnnouncementAnchor from '@/components/Announcements/AnnouncementAnchor';
import {
  OrderActionNotice,
  StandingOrderActionKind,
  StandingOrderActionLogEntry,
  actionLogUrl,
  canCancel,
  canEdit,
  canToggle,
  getOrderState,
  parseAutoFreezeDetails,
} from '../standingOrderActions';
import {
  BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY,
  canCancelBank,
  canEditBank,
  canFreezeBank,
  canReactivateBank,
  getBankOrderState,
  toOrderState,
} from '../bankStandingOrderActions';
import CardUpdateLink from './CardUpdateLink';
import OrderStatusPanel from './OrderStatusPanel';
import OrderActionLog from './OrderActionLog';
import styles from './AdditionalInfo.module.css';

interface AdditionalInfoProps {
  data: NedarimStandingOrderDtoIn;
  fundId: number;
  handleXclick: () => void;
  /** The charge dialog is mounted once, by the page, so the board can open it too. */
  onCharge: (order: NedarimStandingOrderDtoIn) => void;
  /**
   * HAT-1317: freeze, reactivate, edit and cancel, when the "standing-order-actions" flag is on.
   * The dialogs are mounted by the page, like the charge's, because the row menu opens them too.
   */
  hasOrderActions?: boolean;
  /** HAT-1441: the same actions on a bank order in APT, when the "bank-standing-order-actions" flag is on. */
  hasBankActions?: boolean;
  /**
   * The collection the office has locked for its bank file that this bank order collects on, if any
   * (bankStandingOrderActions.lockedCollection): until it has passed the blade offers no change.
   */
  collectionLock?: Date | null;
  onAction?: (kind: StandingOrderActionKind) => void;
  /** What the last action on this order did, said at the top of the tab until dismissed. */
  notice?: OrderActionNotice | null;
  onDismissNotice?: () => void;
}

/*
 * Drawn the way Alert draws its own: the 24 grid and 1.5 round stroke of assets/icons, but in
 * currentColor, so each status carries its own tone from the CSS rather than shipping three
 * differently coloured files.
 */
const HISTORY_STATUS_GLYPHS: Record<string, ReactNode> = {
  succeeded: (
    <path d="M5 12.6 9.5 17 19 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  ),
  rejected: (
    <path d="M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  ),
  cancelled: (
    <path d="M6 12h12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  ),
};

/** wa.me wants digits only, with the country code and no leading zero. */
function toInternationalPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('972')) return digits;
  return `972${digits.replace(/^0/, '')}`;
}

const AdditionalInfo = ({
  data, fundId, handleXclick, onCharge, hasOrderActions = false, hasBankActions = false, collectionLock = null, onAction, notice,
  onDismissNotice,
}: AdditionalInfoProps) => {
  const { i18n, t } = useTranslation();

  /*
   * An order the accounting system holds (HAT-1135) has no Nedarim behind it: its history comes
   * from the office's own rows, and the Nedarim-only actions - charge now, card-update link - do
   * not exist for it. Both a bank order and one of the office's own credit orders read this way.
   */
  const isApt = isAptOrder(data);
  const isCreditOrder = data.type === STANDING_ORDER_TYPE_CREDIT;
  const isBankOrder = data.type === STANDING_ORDER_TYPE_BANK;
  const isNedarimCredit = isCreditOrder && !isApt;
  /*
   * A bank order in APT is frozen, edited and cancelled in APT itself (HAT-1441). Where it stands for
   * those actions: a stop the office made is the office's, and has no panel - its alert says it.
   */
  const bankActions = isApt && isBankOrder && hasBankActions;
  const bankState = getBankOrderState(data);
  const bankPanelState = bankActions ? toOrderState(bankState) : null;
  // The office is sending this order's collection to the bank: it says so, and offers nothing until the day has passed.
  const bankLocked = bankActions && collectionLock != null;
  const onBankAction = bankLocked ? undefined : onAction;
  const hasLog = (isNedarimCredit && hasOrderActions) || bankActions;

  const creditUrl = isNedarimCredit
    ? `nedarim-standing-orders/${fundId}/credit/${data.standingOrderId}/details?mosadSource=${data.mosadSource}`
    : '';
  const bankUrl = isBankOrder && !isApt
    ? `nedarim-standing-orders/${fundId}/bank/${data.standingOrderId}/details?mosadSource=${data.mosadSource}&creationDate=${parseDate(data.date ?? null)?.toISOString() ?? ''}`
    : '';
  const aptUrl = isApt ? `funds/financial-data/${fundId}/debits/${data.standingOrderId}/history` : '';

  const {
    data: creditDetails,
    isLoading: isCreditLoading,
    isError: isCreditError,
    refetch: refetchCredit,
  } = useGetAuthApi<CreditStandingOrderDetailsDtoIn>(creditUrl);
  const {
    data: bankDetails,
    isLoading: isBankLoading,
    isError: isBankError,
    refetch: refetchBank,
  } = useGetAuthApi<BankStandingOrderDetailsDtoIn>(bankUrl);
  const {
    data: aptHistory,
    isLoading: isAptLoading,
    isError: isAptError,
    refetch: refetchApt,
  } = useGetAuthApi<DebitHistoryItem[]>(aptUrl);

  /*
   * The bank and branch names for an accounting-system bank order, from the same public list the
   * donation form picks from. They go in the account line's tooltip only: the numbers are what a
   * coordinator reads out, the names are there for the one who asks "which bank is 52".
   */
  const { data: banks } = useQuery({
    queryKey: ['banks-directory'],
    queryFn: useBanks.getBanks,
    enabled: isApt && isBankOrder,
    staleTime: Infinity,
  });
  const bankEntry = data.aptBank && banks ? banks[parseInt(data.aptBank, 10)] : undefined;
  const branchEntry = bankEntry && data.aptBranch
    ? bankEntry.branches?.find((b) => b.id === parseInt(data.aptBranch ?? '', 10))
    : undefined;
  const bankNamesTitle = [bankEntry?.name, branchEntry?.name].filter(Boolean).join(' · ') || undefined;

  /*
   * Everything done to the order from the board (HAT-1317). Read from our own records, so it
   * costs nothing against Nedarim's hourly limit; it also says who froze a frozen order.
   */
  const { data: actionLog } = useGetAuthApi<StandingOrderActionLogEntry[]>(
    hasLog ? actionLogUrl(fundId, data) : '',
  );

  const isDetailsLoading = isCreditLoading || isBankLoading || isAptLoading;
  const isDetailsError = isCreditError || isBankError || isAptError;
  const retryDetails = () => {
    if (isCreditError) refetchCredit();
    if (isBankError) refetchBank();
    if (isAptError) refetchApt();
  };

  const formatCurrency = (value: number) => formatRowMoney(value, data, i18n.language);

  const clientName = (isCreditOrder ? creditDetails?.clientName : null) ?? data.clientName;
  const amount = (isCreditOrder ? creditDetails?.amount : null) ?? data.amount;

  const createdDateStr = isCreditOrder && creditDetails ? creditDetails.createdDate : data.creationDate;
  const creationDateParsed = parseDate(createdDateStr ?? null);
  const creationDate = creationDateParsed ? formatDate(creationDateParsed, i18n.language) : null;

  const nextDateStr = isCreditOrder && creditDetails ? creditDetails.nextDate : data.nextDate;
  const remaining = isCreditOrder && creditDetails ? creditDetails.remainingPayments : data.remainingPayments;
  const paymentsPaid = getPaymentsPaid(data);

  let endDate: Date | null = null;
  // An order that runs until the donor stops has no end date to show.
  if (nextDateStr && remaining && remaining > 0 && !isUnlimited(remaining)) {
    const nextDate = parseDate(nextDateStr);
    if (nextDate) {
      nextDate.setMonth(nextDate.getMonth() + (remaining - 1));
      endDate = nextDate;
    }
  }

  const hasDateRange = creationDate && endDate;

  const nextDateParsed = parseDate(nextDateStr ?? null);
  const nextDateFormatted = nextDateParsed ? formatDate(nextDateParsed, i18n.language) : null;

  // The details are read live and carry Nedarim's own status; the row can be minutes old.
  const orderState = bankPanelState
    ?? getOrderState({ ...data, remainingPayments: remaining ?? data.remainingPayments }, creditDetails?.status);

  /*
   * If the order is frozen because somebody froze it here, say who and when. The newest freeze or
   * reactivation that went through decides it; an order frozen in Nedarim's own interface has no
   * such line and is simply "frozen".
   */
  const stoppedBy = (action: 'freeze' | 'cancel') => {
    // An automatic freeze (HAT-1447) is a freeze too, by the declined-orders check rather than a person.
    const isStop = (kind: string) => kind === action || (action === 'freeze' && kind === 'auto-freeze');
    const last = actionLog?.find((entry) => entry.succeeded && (isStop(entry.action) || entry.action === 'reactivate'));
    if (!last || !isStop(last.action)) return null;
    const at = new Date(last.requestedAt);
    if (isNaN(at.getTime())) return null;
    return last.action === 'auto-freeze'
      ? { date: formatDate(at, i18n.language), by: null, autoMonths: parseAutoFreezeDetails(last.details).count }
      : { date: formatDate(at, i18n.language), by: last.requestedBy };
  };
  const frozen = orderState === 'disabled' ? stoppedBy('freeze') : null;
  // A bank order cancelled here stays on the board to be reactivated (HAT-1441), so it says who cancelled it.
  const cancelledBy = orderState === 'cancelled' && bankActions ? stoppedBy('cancel') : null;

  const noticeText = (() => {
    if (!notice) return null;
    // The credit words, but an edit is saved in APT rather than at Nedarim.
    if (notice.kind === 'freeze') return t('standingOrders.orderActions.frozenNotice');
    if (notice.kind === 'edit') return t(isApt ? 'standingOrders.orderActions.bank.editedNotice' : 'standingOrders.orderActions.editedNotice');
    const date = parseDate(notice.nextDate ?? null);
    return date
      ? t('standingOrders.orderActions.reactivatedNotice', { date: formatDate(date, i18n.language) })
      : t('standingOrders.orderActions.reactivatedNoticeNoDate');
  })();

  const creditHistory = isNedarimCredit ? creditDetails?.historyItems : null;
  // The accounting rows are drawn with the bank table whatever the order's payment method: date,
  // amount, what happened, and the receipt or the bank's reason.
  const bankHistory = isApt
    ? (aptHistory ? aptHistoryToBankHistory(aptHistory, t) : null)
    : isBankOrder ? bankDetails?.historyItems : null;

  /*
   * What the order will have brought in when it ends: what came in so far, plus the payments
   * left at the monthly amount. Only for an order that does end. An unlimited order has no end
   * to project to - its "remaining" is the 999 that means "no limit", and multiplying that by
   * the amount printed a figure like 1,098,900 ₪ that nothing will ever collect.
   *
   * "So far" is what actually stayed with the fund: the charges that went through, less what
   * the bank returned. Nedarim's credit history is summed here; the accounting system's orders
   * carry the same net figure on the row.
   */
  const paidSoFar = isApt
    ? (getTotalPaid(data) ?? 0)
    : (creditHistory
      ?.filter((item) => item.status === 'Succeeded')
      .reduce((sum, item) => sum + (parseFloat(item.amount ?? '0') || 0), 0) ?? 0);
  const expectedTotal = remaining != null && !isUnlimited(remaining)
    ? paidSoFar + (remaining > 0 ? remaining * amount : 0)
    : null;

  const lastChargeFailed = getLastChargeStatus(data) === 'failed';

  /*
   * When the failure happened. The board cannot say: a rejected charge is never written to the
   * transactions its dates come from, and Nedarim's list of orders carries no date of its own -
   * so the column there shows why and not when. This panel can, because it has already fetched
   * the order's own history, where the rejected attempts are dated. A bank order keeps using the
   * transmission date, which is what its last-transaction field holds.
   */
  const lastRejectionDate = (() => {
    if (!creditHistory) return null;

    let latest: Date | null = null;
    for (const item of creditHistory) {
      if (item.status !== 'Rejected') continue;
      const parsed = parseDate(item.date ?? null);
      if (parsed && (latest === null || parsed > latest)) latest = parsed;
    }
    return latest;
  })();

  const failureDateParsed = isBankOrder || isApt ? parseDate(data.lastTransactionDate) : lastRejectionDate;
  const failureDate = failureDateParsed ? formatDate(failureDateParsed, i18n.language) : null;
  // Nedarim's credit rows carry the reason; so do the accounting rows, with the bank's own words.
  const failureReason = isCreditOrder || isApt ? (data.errorMessage || null) : null;

  /*
   * An order the office stopped, and why (HAT-1439). The board says «מוקפאת» on the row, but the
   * blade never said it at all, and the office's reason sat under «פרטים נוספים» as «סטטוס
   * במשרד». The day is the one APT recorded with the status; most older orders have none.
   */
  // A freeze made from the board is said by the status panel, with who froze it and when (HAT-1441).
  const aptStopped = isApt && getActiveStatus(data) === 'disabled' && !(bankActions && data.aptFrozenFromSite);
  const aptStatusDateParsed = isApt ? parseDate(data.aptStatusDate ?? null) : null;
  const aptStatusDate = aptStatusDateParsed ? formatDate(aptStatusDateParsed, i18n.language) : null;
  const aptStoppedMessage = (() => {
    if (!aptStopped) return null;
    const status = t(activeStatusKey('disabled', hasOrderActions || hasBankActions));
    const reason = data.aptFrozenFromSite
      ? (data.aptBoardStopReason
        ? t('standingOrders.aptFrozenFromSiteWithReason', { reason: data.aptBoardStopReason })
        : t('standingOrders.aptFrozenFromSite'))
      : data.aptStopReason;
    if (reason) {
      return aptStatusDate
        ? t('standingOrders.aptStoppedAlert', { status, date: aptStatusDate, reason })
        : t('standingOrders.aptStoppedAlertNoDate', { status, reason });
    }
    return aptStatusDate
      ? t('standingOrders.aptStoppedAlertNoReason', { status, date: aptStatusDate })
      : t('standingOrders.aptStoppedAlertBare', { status });
  })();

  /*
   * What the history adds up to, above the table. Only the charges that went through are
   * summed - a rejected charge took no money, and adding it in overstated what the donor
   * has given.
   */
  const historyStats = (() => {
    // The accounting system's history: a charge is money in, a return is money the bank took back.
    /*
     * Counted the way the order's own counters are (Chaya, 4.10.2026): the table lists a return
     * as a row of its own, beside the charge it took back, so "20 rows" is 19 collections and one
     * return - and the order has 18 payments to show for it, not 20. The summary says exactly
     * that, and its net figure is the «שולם סה"כ» in the next tab.
     */
    if (isApt) {
      if (!aptHistory) return null;
      // Each row is a collection; a returned one is marked on its own row (the server puts the
      // return there). So the rows, the «גביות» count and the payments line agree: 3 rows, 3
      // collections, one returned, 2 paid. A return the server could not place stands alone with
      // a negative amount: it is a return, not a collection.
      let kept = 0;
      let returned = 0;
      let returnedCount = 0;
      let chargedCount = 0;
      for (const item of aptHistory) {
        if (item.amount > 0) chargedCount++;
        if (item.kind === 'Returned') { returned += Math.abs(item.amount); returnedCount++; }
        else kept += item.amount;
      }
      return {
        count: chargedCount, succeeded: kept, failed: returned,
        returnedCount, kept: chargedCount - returnedCount,
      };
    }
    if (!creditHistory) return null;
    let succeeded = 0;
    let failed = 0;
    for (const item of creditHistory) {
      const value = parseFloat(item.amount ?? '0') || 0;
      if (item.status === 'Succeeded') succeeded += value;
      else if (item.status === 'Rejected') failed += value;
    }
    return { count: creditHistory.length, succeeded, failed };
  })();

  const phone = (isCreditOrder ? creditDetails?.phone : null) ?? data.phone;
  const mail = (isCreditOrder ? creditDetails?.mail : null) ?? data.email;

  /*
   * The donor number under the name (HAT-1488): a fund can have two «פרידמן יהודה», and the blade
   * is opened to find out which one this is - so it is in the header, not a tab away.
   */
  const donorNumber = isApt ? data.aptContactId : null;
  const headerTop = (
    <div className="flex flex-col">
      <span className="body-lr-regular">
        {t('standingOrders.standingOrder')} - {clientName}
      </span>
      {donorNumber != null && (
        <span className="body-sm-regular">
          {t('standingOrders.donorNumber')}: {donorNumber}
        </span>
      )}
    </div>
  );

  const headerBottom = (
    <>
      <span className={`${styles.amount} body-sm-medium text-(--font-tertiary) bg-(--surface-tertiary-low)`}>
        <img src={donation} alt="" />
        {formatCurrency(amount)}
      </span>
      {hasDateRange && (
        <span className="body-sm-regular" dir="ltr">
          {creationDate} - {formatDate(endDate!, i18n.language)}
        </span>
      )}
    </>
  );

  /*
   * Nedarim return the credit history as strings - "3600" and "20/08/2026" - and the rows
   * showed them as they came, next to a header reading "3,600.00 ₪" and a board reading
   * 20.08.2026. Both are formatted like the rest of the blade now; a value that does not
   * parse is shown as received rather than dropped.
   */
  const formatHistoryDate = (value: string | null | undefined) => {
    const parsed = parseDate(value ?? null);
    return parsed ? formatDate(parsed, i18n.language) : (value ?? '');
  };

  const formatHistoryAmount = (value: string | null | undefined) => {
    const parsed = parseFloat(value ?? '');
    return isNaN(parsed) ? (value ?? '') : formatCurrency(parsed);
  };

  const historyStatusLabel = (status: string | null | undefined) => {
    const key = `standingOrders.status_${status?.toLowerCase()}`;
    // An unknown status from Nedarim used to print the translation key itself.
    return status && i18n.exists(key) ? t(key) : (status ?? '');
  };

  /*
   * The status column was the same word down the whole table - "הצליח" eighty-odd times in a
   * panel 456px wide - and the one column that says something different, the rejection text,
   * was the one being squeezed. The word is a glyph now, keeping its wording on the tooltip
   * and for screen readers, and the width it gave up went to the notes.
   *
   * A status Nedarim send that we have no glyph for stays a word rather than disappearing.
   */
  const renderHistoryStatus = (status: string | null | undefined) => {
    const key = status?.toLowerCase() ?? '';
    const label = historyStatusLabel(status);
    const glyph = HISTORY_STATUS_GLYPHS[key];

    if (!glyph) return label;

    return (
      <span className={`${styles.statusIcon} ${styles[`status_${key}`] ?? ''}`} title={label}>
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          {glyph}
        </svg>
        <span className="sr-only">{label}</span>
      </span>
    );
  };

  const renderCreditRow = (item: CreditHistoryItemDtoIn, index: number) => {
    const isRejected = item.status === 'Rejected';
    return (
      <tr key={index} className={isRejected ? styles.rowRejected : ''}>
        <td className={`body-sm-regular ${styles.td}`}>{formatHistoryDate(item.date)}</td>
        <td className={`body-sm-regular ${styles.td}`}>{formatHistoryAmount(item.amount)}</td>
        <td className={`body-sm-regular ${styles.td} ${styles.tdStatus}`}>
          {renderHistoryStatus(item.status)}
        </td>
        <td className={`body-sm-regular ${styles.td} ${styles.tdNotes}`}>
          {isRejected ? item.clientNameOrErrorText : ''}
        </td>
      </tr>
    );
  };

  const renderAptRow = (item: DebitHistoryItem, index: number) => {
    const returned = item.kind === 'Returned';
    const key = returned ? 'rejected' : 'succeeded';
    const label = t(returned ? 'standingOrders.historyReturned' : 'standingOrders.historyCharged');
    const [row] = aptHistoryToBankHistory([item], t);
    return (
      <tr key={index} className={returned ? styles.rowRejected : ''}>
        <td className={`body-sm-regular ${styles.td}`}>{formatHistoryDate(row.date)}</td>
        <td className={`body-sm-regular ${styles.td}`}>{formatCurrency(Math.abs(item.amount))}</td>
        <td className={`body-sm-regular ${styles.td} ${styles.tdStatus}`}>
          <span className={`${styles.statusIcon} ${styles[`status_${key}`] ?? ''}`} title={label}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              {HISTORY_STATUS_GLYPHS[key]}
            </svg>
            <span className="sr-only">{label}</span>
          </span>
        </td>
        <td className={`body-sm-regular ${styles.td} ${styles.tdNotes}`}>{row.comments}</td>
      </tr>
    );
  };

  const renderBankRow = (item: BankHistoryItemDtoIn, index: number) => (
    // A return from the bank reads in the error tone, like a rejected credit charge.
    <tr key={index} className={item.amount < 0 ? styles.rowRejected : ''}>
      <td className={`body-sm-regular ${styles.td}`}>{formatHistoryDate(item.date)}</td>
      <td className={`body-sm-regular ${styles.td}`}>{formatCurrency(item.amount)}</td>
      <td className={`body-sm-regular ${styles.td}`}>{item.transactionType}</td>
      <td className={`body-sm-regular ${styles.td} ${styles.tdNotes}`}>{item.comments}</td>
    </tr>
  );

  /**
   * The card or account the money comes from, which the board has no room for and the blade
   * simply did not show - and the expiry alongside it, because a card running out is the most
   * common reason a standing order starts failing and the fund only ever heard about it from
   * the rejection.
   */
  const renderPaymentSource = () => {
    const source = getPaymentSourceLabel(data);
    const expiry = formatCardExpiry(data.validity);
    const expiryState = getCardExpiryState(data.validity);

    if (!source && !expiry && !data.billingDay && !isCreditOrder) {
      return (
        <span className="body-md-regular text-(--font-body-3)">
          {t('standingOrders.noPaymentDetails')}
        </span>
      );
    }

    /*
     * A grid of label-over-value, rather than four sentences down the panel. The terminal is
     * in the DTO and was never shown; a coordinator on the phone to Nedarim is asked for it.
     *
     * A bank account from the accounting system is one line with each number named in it -
     * «בנק 52 · סניף 177 · חשבון 111597» - under one label (Chaya, 4.10.2026). «52-177-111597»
     * under «חשבון» read as if all of it were the account number; a labelled cell per part was
     * too much for what is one fact. The holder is shown only when it is not the donor.
     */
    const cells: { key: string; value: string; label: string; tone?: string; wide?: boolean; title?: string }[] = [];
    const aptBankParts = isApt && isBankOrder && (data.aptBank || data.aptBranch || data.aptAccount);
    if (aptBankParts) {
      const parts = [
        data.aptBank && t('standingOrders.accountPartBank', { value: data.aptBank }),
        data.aptBranch && t('standingOrders.accountPartBranch', { value: data.aptBranch }),
        data.aptAccount && t('standingOrders.accountPartAccount', { value: data.aptAccount }),
      ].filter(Boolean);
      cells.push({ key: 'account', value: parts.join(' · '), label: t('standingOrders.bankAccountFull'), wide: true, title: bankNamesTitle });
      const norm = (v: string | null | undefined) => (v ?? '').replace(/\s+/g, ' ').trim();
      if (data.aptAccountHolder && norm(data.aptAccountHolder) !== norm(data.clientName)) {
        cells.push({ key: 'holder', value: data.aptAccountHolder, label: t('standingOrders.accountHolder') });
      }
    } else if (source) {
      cells.push({
        key: 'source',
        value: source,
        label: t(isCreditOrder ? 'standingOrders.card' : 'standingOrders.bankAccount'),
      });
    }
    if (expiry) {
      cells.push({
        key: 'expiry',
        value: expiry,
        label: t('standingOrders.cardExpiry'),
        tone: expiryState === 'expired' ? 'expired' : expiryState === 'expiringSoon' ? 'warn' : undefined,
      });
    }
    if (data.billingDay) {
      cells.push({
        key: 'billingDay',
        value: t('standingOrders.billingDayOfMonth', { day: data.billingDay }),
        label: t('standingOrders.billingDayLabel'),
      });
    }
    if (isNedarimCredit && data.terminalId) {
      cells.push({ key: 'terminal', value: data.terminalId, label: t('standingOrders.terminal') });
    }

    return (
      <div className="flex flex-col gap-3">
        <div className={styles.kvGrid}>
          {cells.map((cell) => (
            <div key={cell.key} className={styles.kvCell} style={cell.wide ? { gridColumn: '1 / -1' } : undefined}>
              <small className={styles.kvLabel}>{cell.label}</small>
              <span className={`body-md-regular ${cell.tone ? styles[`kv_${cell.tone}`] : ''}`} title={cell.title}>
                {cell.value}
                {cell.key === 'expiry' && expiryState === 'expiringSoon' && (
                  <span className={styles.expiryWarning}>{t('standingOrders.expiringSoon')}</span>
                )}
                {cell.key === 'expiry' && expiryState === 'expired' && (
                  <span className={styles.expiryExpired}>{t('standingOrders.expired')}</span>
                )}
              </span>
            </div>
          ))}
        </div>

        {isNedarimCredit && (
          <CardUpdateLink
            fundId={fundId}
            standingOrderId={data.standingOrderId}
            mosadSource={data.mosadSource}
            whatsappNumber={phone ? toInternationalPhone(phone) : null}
            email={mail}
          />
        )}
      </div>
    );
  };

  const renderHistoryTable = () => {
    if (isDetailsLoading) {
      return (
        <TextRowsSkeleton
          rowsGap={12}
          rows={[{ width: '100%' }, { width: '90%' }, { width: '95%' }, { width: '80%' }]}
        />
      );
    }

    // Without this an unreachable Nedarim looked exactly like a donor who has never been
    // charged, and the coordinator would have believed it.
    if (isDetailsError) {
      return (
        <div className="flex flex-col items-start gap-2">
          <span className="body-md-regular text-(--status-rejected-text)">
            {t('standingOrders.detailsFailed')}
          </span>
          <button type="button" className={styles.retryLink} onClick={retryDetails}>
            {t('retry')}
          </button>
        </div>
      );
    }

    if (isNedarimCredit && creditHistory && creditHistory.length > 0) {
      return (
        <table className={styles.historyTable}>
          <thead className={styles.thead}>
            <tr>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.date')}</th>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.amount')}</th>
              <th className={`${styles.th} ${styles.thStatus} body-xs-sm-bold`}>{t('standingOrders.status')}</th>
              <th className={`${styles.th} ${styles.thNotes} body-xs-sm-bold`}>{t('standingOrders.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {creditHistory.map(renderCreditRow)}
          </tbody>
        </table>
      );
    }

    /*
     * The accounting system's history is drawn exactly like the credit history (Chaya, 4.10.2026:
     * "look at the credit blade"): date, amount, a glyph for what happened, and the one column
     * that can run long - the receipt, or the bank's reason for a return. It had been laid out as
     * the old Nedarim bank table, with a word where the glyph goes, and the amount ran into it.
     */
    if (isApt && aptHistory && aptHistory.length > 0) {
      return (
        <table className={styles.historyTable}>
          <thead className={styles.thead}>
            <tr>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.date')}</th>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.amount')}</th>
              <th className={`${styles.th} ${styles.thStatus} body-xs-sm-bold`}>{t('standingOrders.status')}</th>
              <th className={`${styles.th} ${styles.thNotes} body-xs-sm-bold`}>{t('standingOrders.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {aptHistory.map(renderAptRow)}
          </tbody>
        </table>
      );
    }

    if (isBankOrder && bankHistory && bankHistory.length > 0) {
      return (
        <table className={styles.historyTable}>
          <thead className={styles.thead}>
            <tr>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.date')}</th>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.amount')}</th>
              <th className={`${styles.th} body-xs-sm-bold`}>{t('standingOrders.transactionType')}</th>
              <th className={`${styles.th} ${styles.thNotes} body-xs-sm-bold`}>{t('standingOrders.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {bankHistory.map(renderBankRow)}
          </tbody>
        </table>
      );
    }

    return <span className="body-md-regular text-(--font-body-2)">{t('standingOrders.noChargeHistory')}</span>;
  };

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="456px">
      <Tabs defaultValue="chargesAndAuthorizations">
        <TabsList>
          <TabsTrigger value="chargesAndAuthorizations">
            {t(isApt ? 'standingOrders.chargesAndReturns' : 'standingOrders.chargesAndAuthorizations')}
          </TabsTrigger>
          <TabsTrigger value="additionalDetails">{t('standingOrders.additionalDetails')}</TabsTrigger>
        </TabsList>
        <TabsContent value="chargesAndAuthorizations" className={`${styles.content} custom-scrollbar`}>
          {/*
            * The blade opened on a charge button and a history table, and left the coordinator
            * to work out from the table that the last charge had failed and why. It says so.
            * For an accounting-system order the charge did not fail, the bank returned it.
            */}
          {aptStoppedMessage && <Alert type="warning" message={aptStoppedMessage} />}
          {lastChargeFailed && isApt && (
            <Alert
              type="error"
              message={failureReason
                ? t('standingOrders.lastReturnAlert', { date: failureDate ?? '', reason: failureReason })
                : t('standingOrders.lastReturnAlertNoReason', { date: failureDate ?? '' })}
            />
          )}
          {lastChargeFailed && !isApt && (
            <Alert
              type="error"
              message={failureDate
                ? t('standingOrders.lastChargeFailedAlert', {
                  date: failureDate,
                  reason: failureReason ?? t('standingOrders.lastCharge_failed'),
                })
                : t('standingOrders.lastChargeFailedAlertNoDate', {
                  reason: failureReason ?? t('standingOrders.lastCharge_failed'),
                })}
            />
          )}
          {isNedarimCredit && hasOrderActions ? (
            <>
              {noticeText && <Alert type="success" message={noticeText} onClose={onDismissNotice} />}
              <OrderStatusPanel
                state={orderState}
                nextDate={nextDateFormatted}
                amount={formatCurrency(amount)}
                frozen={frozen}
                onToggle={canToggle(orderState) && onAction
                  ? () => onAction(orderState === 'disabled' ? 'reactivate' : 'freeze')
                  : undefined}
              />
              {orderState !== 'cancelled' && (
                <div className={styles.actionRow}>
                  <button
                    className={styles.standingOrderChargeButton}
                    onClick={() => onCharge({ ...data, amount })}
                  >
                    {t('standingOrders.creditStandingOrderCharge.button')}
                  </button>
                  {canEdit(orderState) && onAction && (
                    <button className={styles.standingOrderChargeButton} onClick={() => onAction('edit')}>
                      {t('standingOrders.orderActions.edit')}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : bankPanelState ? (
            /*
             * The same panel as a credit order's (HAT-1441), without the one-off charge - the office
             * collects a bank order, the site does not. A cancelled one stays, with who cancelled it
             * and the button that reactivates it.
             */
            <>
              {noticeText && <Alert type="success" message={noticeText} onClose={onDismissNotice} />}
              {bankLocked && collectionLock && (
                <Alert
                  type="info"
                  message={t('standingOrders.orderActions.bank.locked.alert', { date: formatDate(collectionLock, i18n.language) })}
                />
              )}
              {/* The actions' announcement (HAT-1489) points here while the order is open: on a phone,
                  where a row has no menu, this is the only place they are. */}
              <AnnouncementAnchor name={BANK_STANDING_ORDER_ACTIONS_FEATURE_KEY}>
                <OrderStatusPanel
                  bank
                  state={bankPanelState}
                  nextDate={nextDateFormatted}
                  amount={formatCurrency(amount)}
                  frozen={frozen}
                  cancelled={cancelledBy}
                  reason={data.aptBoardStopReason}
                  onToggle={onBankAction && (canFreezeBank(bankState) || canReactivateBank(bankState, data))
                    ? () => onBankAction(canReactivateBank(bankState, data) ? 'reactivate' : 'freeze')
                    : undefined}
                />
              </AnnouncementAnchor>
              {canEditBank(bankState) && onBankAction && (
                <div className={styles.actionRow}>
                  <button className={styles.standingOrderChargeButton} onClick={() => onBankAction('edit')}>
                    {t('standingOrders.orderActions.edit')}
                  </button>
                </div>
              )}
            </>
          ) : (isCreditOrder || isBankOrder) && !isApt && (
            <button
              className={styles.standingOrderChargeButton}
              onClick={() => onCharge({ ...data, amount })}
            >
              {t(`standingOrders.${isCreditOrder ? 'credit' : 'bank'}StandingOrderCharge.button`)}
            </button>
          )}
          <BladeSimpleSection title={t('standingOrders.paymentDetails')}>
            {renderPaymentSource()}
          </BladeSimpleSection>
          <BladeSimpleSection title={t(isApt ? 'standingOrders.chargesAndReturns' : 'standingOrders.chargeHistory')}>
            {/*
              * Read as a sum: what was collected, what the bank took back, what stayed - the last
              * figure is «שולם סה"כ» in the next tab. A return is a noun here («החזרה אחת»,
              * «2 החזרות») so one and many read equally well, and only it is in the error tone.
              */}
            {isApt && historyStats && historyStats.count > 0 && (
              <div className={styles.historySummary}>
                <span>{t('standingOrders.aptHistoryCollections', { count: historyStats.count })}</span>
                {(historyStats.returnedCount ?? 0) > 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className={styles.historySummaryFailed}>
                      {t('standingOrders.aptHistoryReturns', {
                        count: historyStats.returnedCount,
                        amount: formatCurrency(historyStats.failed),
                      })}
                    </span>
                  </>
                )}
                <span aria-hidden="true">·</span>
                <span>{t('standingOrders.aptHistoryNet', { amount: formatCurrency(historyStats.succeeded) })}</span>
              </div>
            )}
            {!isApt && historyStats && historyStats.count > 0 && (
              <div className={styles.historySummary}>
                <span>
                  {t('standingOrders.historySummary', {
                    count: historyStats.count,
                    succeeded: formatCurrency(historyStats.succeeded),
                  })}
                </span>
                {historyStats.failed > 0 && (
                  <span className={styles.historySummaryFailed}>
                    {t('standingOrders.historySummaryFailed', {
                      amount: formatCurrency(historyStats.failed),
                    })}
                  </span>
                )}
              </div>
            )}
            {renderHistoryTable()}
          </BladeSimpleSection>
          {/* Away from the everyday buttons on purpose: this one cannot be undone. */}
          {((isNedarimCredit && hasOrderActions && canCancel(orderState)) || (bankActions && !bankLocked && canCancelBank(bankState))) && onAction && (
            <button type="button" className={styles.cancelLink} onClick={() => onAction('cancel')}>
              {t('standingOrders.orderActions.cancel')}
            </button>
          )}
        </TabsContent>
        <TabsContent value="additionalDetails" className={`${styles.content} custom-scrollbar`}>
          <BladeSimpleSection title={t(isApt ? 'standingOrders.aptOrderId' : 'standingOrders.nedarimPlusStandingOrderId')}>
            <span className="body-md-regular text-(--font-body-1) flex items-center gap-2">
              {data.standingOrderId}
              {/* Read out to Nedarim or to the office on the phone, or pasted into a ticket. */}
              <CopyButton value={String(data.standingOrderId)} />
            </span>
          </BladeSimpleSection>
          {/* The office's own words on the order («אושר בבנק», «ביטול הרשאה»…), as they wrote them. */}
          {isApt && data.aptStatus && (
            <BladeSimpleSection title={t('standingOrders.aptStatus')}>
              <span className="body-md-regular text-(--font-body-1)">
                {data.aptStatus}
                {aptStatusDate && <span className="text-(--font-body-3)">{' · '}{aptStatusDate}</span>}
              </span>
            </BladeSimpleSection>
          )}
          <BladeSimpleSection title={t('standingOrders.nextPaymentDate')}>
            <span className="body-md-regular text-(--font-body-1)">
              {nextDateFormatted ?? '-'}
            </span>
          </BladeSimpleSection>
          {/*
            * How far along the order is and when it started and ends. All of it was already
            * on the board row or derivable from it, and none of it was in the blade - so the
            * blade, which is where a coordinator goes for the detail, had less than the row.
            */}
          <BladeSimpleSection title={t('standingOrders.payments')}>
            <span className="body-md-regular text-(--font-body-1)">
              {remaining == null || isUnlimited(remaining)
                ? t('standingOrders.paidUnlimitedLong', { paid: paymentsPaid })
                : t('standingOrders.paidOfLong', { paid: paymentsPaid, total: getPaymentsTotal(data) ?? paymentsPaid + Math.max(remaining, 0) })}
              {/* "18 paid" next to 19 collections in the history: the difference is the returns, said here. */}
              {isApt && (data.numberOfReturns ?? 0) > 0 && (
                <span className="text-(--status-rejected-text)">
                  {' · '}{t('standingOrders.paymentsReturns', { count: data.numberOfReturns ?? 0 })}
                </span>
              )}
            </span>
          </BladeSimpleSection>
          {creationDate && (
            <BladeSimpleSection title={t('standingOrders.creationDate')}>
              <span className="body-md-regular text-(--font-body-1)">{creationDate}</span>
            </BladeSimpleSection>
          )}
          {endDate && (
            <BladeSimpleSection title={t('standingOrders.estimatedEndDate')}>
              <span className="body-md-regular text-(--font-body-1)">{formatDate(endDate, i18n.language)}</span>
            </BladeSimpleSection>
          )}
          {/* HAT-1170: how much has actually come in, apart from how much is still expected.
              The charges that went through, not every row of the history - a rejected charge
              took no money. */}
          {(isNedarimCredit || isApt) && historyStats && (
            <BladeSimpleSection title={t('standingOrders.totalPaid')}>
              <span className="body-md-regular text-(--font-body-1)">{formatCurrency(historyStats.succeeded)}</span>
            </BladeSimpleSection>
          )}
          {expectedTotal != null && (
            <BladeSimpleSection title={t('standingOrders.expectedTotalAmount')}>
              <span className="body-md-regular text-(--font-body-1)">
                {formatCurrency(expectedTotal)}
              </span>
            </BladeSimpleSection>
          )}
          <BladeSimpleSection title={t('standingOrders.donorDetails')}>
            <div className="flex flex-col">
              <span className="body-md-regular text-(--font-body-1)">{clientName}</span>
              {donorNumber != null && (
                <span className="body-md-regular text-(--font-body-2)">
                  {t('standingOrders.donorNumber')}: {donorNumber}
                </span>
              )}
              {/* The id is what the receipt is issued against, and the reason a mistyped one
                  matters - it was in the DTO and nowhere on screen. */}
              {data.israeliId && (
                <span className="body-md-regular text-(--font-body-2)">
                  {t('standingOrders.newOrder.israeliId')}: {data.israeliId}
                </span>
              )}
              {phone && (
                <a
                  className={styles.contactLink}
                  href={`https://wa.me/${toInternationalPhone(phone)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconWithText icon={whatsapp} text={phone} />
                </a>
              )}
              {mail && (
                <a className={styles.contactLink} href={`mailto:${mail}`}>
                  <IconWithText icon={email} text={mail} />
                </a>
              )}
              {data.city && (
                <span className="body-md-regular text-(--font-body-2)">{data.city}</span>
              )}
            </div>
          </BladeSimpleSection>
          {/*
            * The free text somebody typed into Nedarim against this order. It has been in the
            * DTO all along, and the global search already looks through it, but it was never
            * shown - so a coordinator could find an order by a word in its note and then not
            * see the note. On the parent mosad it matters more than that: this is the only
            * place the fund an order belongs to is written down, so it is where you look when
            * an order is filed somewhere surprising.
            */}
          {data.comments && (
            <BladeSimpleSection title={t('standingOrders.notes')}>
              <span className={styles.comments}>{data.comments}</span>
            </BladeSimpleSection>
          )}
          {hasLog && <OrderActionLog entries={actionLog} />}
        </TabsContent>
      </Tabs>
    </Blade>
  );
};

export default AdditionalInfo;
